import React, { useEffect, useState } from 'react';
import { useTable, usePagination, useGlobalFilter, useSortBy, useExpanded } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

// Global Search Filter Component
function GlobalFilter({ globalFilter, setGlobalFilter }) {
  return (
    <div className="dataTable_search mb-3">
      <input
        value={globalFilter || ''}
        onChange={(e) => setGlobalFilter(e.target.value)}
        className="form-control"
        placeholder="Search..."
        style={{ maxWidth: '200px' }}
      />
    </div>
  );
}

// Reusable DataTable Component with Expandable Rows
export default function DataTable({
  columns,
  data,
  initialSearchValue,
  renderRowSubComponent,
  expandAll = false
}) {
  const [expandedRows, setExpandedRows] = useState({});

  // Initialize all rows as expanded if expandAll is true
  useEffect(() => {
    if (expandAll && data.length > 0) {
      const initialExpanded = {};
      data.forEach((_, index) => {
        initialExpanded[index] = true;
      });
      setExpandedRows(initialExpanded);
    }
  }, [data, expandAll]);

  const filterAllColumns = (rows, id, filterValue) => {
    if (!filterValue) return rows;
    const lowercasedFilter = filterValue.toLowerCase();

    return rows.filter((row) => {
      return Object.values(row.original).some(
        (value) =>
          value &&
          value.toString().toLowerCase().includes(lowercasedFilter)
      );
    });
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        globalFilter: initialSearchValue,
        expanded: expandedRows
      },
      globalFilter: filterAllColumns,
      autoResetExpanded: false,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );

  // Set the global filter to the initial value when the component mounts
  useEffect(() => {
    if (initialSearchValue) {
      setGlobalFilter(initialSearchValue);
    }
  }, [initialSearchValue, setGlobalFilter]);

  // Update expanded rows when data changes
  useEffect(() => {
    if (expandAll && data.length > 0) {
      const newExpanded = {};
      data.forEach((_, index) => {
        newExpanded[index] = true;
      });
      setExpandedRows(newExpanded);
    }
  }, [data, expandAll]);

  const toggleRowExpanded = (rowIndex) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowIndex]: !prev[rowIndex]
    }));
  };

  return (
    <div className="dataTable_wrapper container-fluid">
      {/* Global Search Filter */}
      <GlobalFilter globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />

      {/* Table */}
      <div className="table-responsive">
        <table {...getTableProps()} className="table table-striped">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} className="dataTable_headerRow">
                {renderRowSubComponent && <th className="dataTable_headerCell"></th>}
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="dataTable_headerCell"
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="dataTable_body">
            {page.map((row, rowIndex) => {
              prepareRow(row);
              const isExpanded = expandedRows[rowIndex];

              return (
                <React.Fragment key={row.id}>
                  <tr {...row.getRowProps()} className="dataTable_row">
                    {renderRowSubComponent && (
                      <td className="dataTable_cell">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => toggleRowExpanded(rowIndex)}
                          style={{
                            padding: '2px 6px',
                            fontSize: '0.75rem'
                          }}
                        >
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </td>
                    )}
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="dataTable_cell">
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                  {renderRowSubComponent && isExpanded && (
                    <tr>
                      <td colSpan={columns.length + 1} className="p-0">
                        {renderRowSubComponent({ row })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="dataTable_pagination">
        <div className="dataTable_pageInfo">
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </div>

        <div className="dataTable_navigation">
          <div className="pagebuttons">
            <button
              className="btn btn-primary me-2 btn1"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              Prev
            </button>
            <button
              className="btn btn-primary btn1"
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              Next
            </button>
          </div>
        </div>

        <div className="dataTable_pageSize">
          <span className="dataTable_showLabel">Show:</span>
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[5, 10, 20].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
